import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Send, XCircle } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS = { pending: "bg-amber-50 text-amber-800 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-rose-50 text-rose-700 border-rose-200" };
export const SubmissionBadge = ({ s }) => (
  <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS[s.status]}`} data-testid={`submission-status-${s.id}`}>
    {s.status === "pending" ? "Awaiting assessment" : s.status === "approved" ? "Approved" : "Rejected"}
  </span>
);

function SubmitForm({ info, onDone }) {
  const [f, setF] = useState({ vessel_name: "", principal_name: "", rank: info.ranks.length === 1 ? info.ranks[0] : "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.vessel_name.trim() || !f.principal_name.trim() || !f.rank) { toast.error("Vessel Name, Principal Name and rank are all required"); return; }
    setSaving(true);
    try { await api.post(`/candidates/${info.cid}/submissions`, f); toast.success("Submitted for Superintendent assessment"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="submission-form">
      <datalist id="submission-vessels">{info.vessels.map((v) => <option key={v} value={v} />)}</datalist>
      <div className="space-y-1.5"><Label htmlFor="sub-vessel">Vessel Name *</Label>
        <Input id="sub-vessel" list="submission-vessels" value={f.vessel_name} onChange={(e) => setF({ ...f, vessel_name: e.target.value })} placeholder="Vessel proposed for" data-testid="submission-vessel-input" /></div>
      <div className="space-y-1.5"><Label>Principal Name *</Label>
        {info.principals.length ? (
          <Select value={f.principal_name} onValueChange={(v) => setF({ ...f, principal_name: v })}>
            <SelectTrigger data-testid="submission-principal-select"><SelectValue placeholder="Select principal" /></SelectTrigger>
            <SelectContent>{info.principals.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : <Input value={f.principal_name} onChange={(e) => setF({ ...f, principal_name: e.target.value })} placeholder="Principal company" data-testid="submission-principal-input" />}</div>
      <div className="space-y-1.5"><Label>Rank applied for *</Label>
        <Select value={f.rank} onValueChange={(v) => setF({ ...f, rank: v })}>
          <SelectTrigger data-testid="submission-rank-select"><SelectValue placeholder="Select rank" /></SelectTrigger>
          <SelectContent>{info.ranks.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select></div>
      <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="submission-submit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Submit for Assessment</>}</Button>
    </form>
  );
}

export function DecisionButtons({ s, onDone }) {
  const [busy, setBusy] = useState("");
  const [approved, setApproved] = useState(null);
  const decide = async (decision) => {
    const note = decision === "reject" ? window.prompt("Reason for rejection (optional)") : null;
    if (decision === "reject" && note === null) return;
    setBusy(decision);
    try {
      const { data } = await api.post(`/submissions/${s.id}/decision`, { decision, note });
      if (decision === "approve") setApproved(data); else { toast.success("Submission rejected"); onDone(); }
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };
  return (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => decide("approve")} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`submission-approve-btn-${s.id}`}>{busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</>}</Button>
      <Button size="sm" variant="outline" onClick={() => decide("reject")} disabled={!!busy} className="text-rose-600" data-testid={`submission-reject-btn-${s.id}`}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
      <AlertDialog open={!!approved} onOpenChange={(o) => { if (!o) { setApproved(null); onDone(); } }}>
        <AlertDialogContent data-testid="approval-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> <span data-testid="approval-confirmation-text">{approved?.message}</span></AlertDialogTitle>
            <AlertDialogDescription>A Proposal Draft has been generated for {s.candidate_name || "this candidate"}. The Sourcing user can now review, edit and email it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction asChild><Link to={`/proposals/${approved?.proposal_id}`} data-testid="approval-open-draft-btn">Open proposal draft</Link></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function ProposalSubmit({ info, onChanged }) {
  const pending = info.submissions.some((s) => s.status === "pending");
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5" data-testid="proposal-submit-panel">
      <div>
        <div className="text-sm font-semibold text-slate-800">Submit for Superintendent assessment</div>
        <div className="text-xs text-slate-500 mt-0.5">On approval a Proposal Draft is generated from this profile, its documents and sea service.</div>
      </div>
      {info.can.submit ? <SubmitForm info={info} onDone={onChanged} />
        : <div className="text-sm text-slate-500" data-testid="submission-unavailable">{pending ? "A submission is awaiting assessment." : info.stage !== "Shortlisted" ? `Only Shortlisted candidates can be submitted (current stage: ${info.stage}).` : "Only the candidate's Sourcing user can submit."}</div>}
      <div className="divide-y divide-slate-100 border-t border-slate-100" data-testid="submission-list">
        {info.submissions.map((s) => (
          <div key={s.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`submission-row-${s.id}`}>
            <div className="text-sm">
              <div className="flex items-center gap-2"><SubmissionBadge s={s} /><span className="font-medium text-slate-800">{s.vessel_name} / {s.principal_name}</span><span className="text-slate-500">· {s.rank}</span></div>
              <div className="text-xs text-slate-400 mt-1">Submitted {new Date(s.submitted_at).toLocaleString()} by {s.submitted_by_name}{s.decided_at && ` · ${s.status} by ${s.decided_by_name}`}{s.decision_note && ` — ${s.decision_note}`}</div>
              {s.approval_text && <div className="text-sm text-emerald-700 font-medium mt-1" data-testid={`submission-approval-text-${s.id}`}>{s.approval_text}</div>}
            </div>
            {s.status === "pending" && info.can.decide && <DecisionButtons s={s} onDone={onChanged} />}
            {s.proposal_id && <Button asChild size="sm" variant="outline" data-testid={`submission-open-draft-${s.id}`}><Link to={`/proposals/${s.proposal_id}`}>Open proposal draft</Link></Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
